/**
 * File: invoiceRecord.ts
 * Path: packages/config/src/invoices/invoiceRecord.ts
 * Description: A saved invoice (invoices/<id>) and its number. Numbers run
 * INV-000001, INV-000002, ... and carry on from the highest one the Zoho
 * history already used, so a number is never given twice. Only the server
 * writes (the saveInvoice callable); staff with the sales permission read.
 */

import { isIsoDate } from "../contracts/contractFormat";
import type { InvoiceDraft } from "./invoiceDraft";

export const INVOICE_COLLECTIONS = {
  invoices: "invoices",
  /** One document (INVOICE_COUNTER_DOC) holding the last number given. */
  counter: "invoiceCounter",
} as const;

export const INVOICE_COUNTER_DOC = "main";

/** How many saved invoices the admin lists at once. */
export const INVOICES_LIST_LIMIT = 500;

export const formatInvoiceNumber = (n: number): string => `INV-${String(n).padStart(6, "0")}`;

/** 12 for "INV-000012" or "zoho:invoice:INV-000012"; null for anything else. */
export const parseInvoiceNumber = (text: string | null | undefined): number | null => {
  const m = /(?:^|:)INV-(\d{1,9})$/.exec((text ?? "").trim());
  return m ? Number(m[1]) : null;
};

/** The highest invoice number among references; 0 when there are none. */
export const highestInvoiceNumber = (refs: Array<string | null | undefined>): number =>
  refs.reduce<number>((max, r) => Math.max(max, parseInvoiceNumber(r) ?? 0), 0);

/**
 * Where a saved invoice stands. "draft": prepared but not issued, so it has
 * no number and is not in the books. "issued": numbered and posted to the
 * books (Dr Trade debtors, Cr Sales). "void": issued but never sent, then
 * cancelled; its number stays, its journal is reversed. "discarded": a
 * draft that was thrown away. Paid, overdue and credited are worked out
 * from the books (invoiceLedger.ts), never stored.
 */
export const SAVED_INVOICE_STATUSES = ["draft", "issued", "void", "discarded"] as const;
export type SavedInvoiceStatus = (typeof SAVED_INVOICE_STATUSES)[number];

/**
 * Why a care plan's monthly draft waits for Liam instead of going out by
 * itself (planBilling.ts has the words): "renewal" the minimum term is over
 * and the plan moves to month-to-month (month 13), "priceChange" the amount
 * differs from the plan's last invoice, "notice" the plan has a cancellation
 * notice, "late" it was drafted after its due date.
 */
export const PLAN_HOLD_REASONS = ["renewal", "priceChange", "notice", "late"] as const;
export type PlanHoldReason = (typeof PLAN_HOLD_REASONS)[number];

/** Which month of a care plan a draft bills (stored as `carePlan` on invoices/<id>). */
export interface CarePlanMonth {
  /** 1 for the plan's first month. */
  month: number;
  /** YYYY-MM-DD, the month's due date as the plan sets it. */
  dueOn: string;
  /** Empty: nothing holds it, so a plan set to send automatically sends it. */
  holds: PlanHoldReason[];
}

export interface SavedInvoice {
  id: string;
  /** "INV-000013"; "" on a draft. */
  number: string;
  status: SavedInvoiceStatus;
  contractId: string | null;
  /** The CRM client it is for, when known. */
  clientId: string | null;
  /** The care plan that drafted it (Phase 2), when it came from one. */
  retainerId: string | null;
  /** The plan month it bills, when a care plan drafted it; null otherwise (absent on older readers). */
  carePlan?: CarePlanMonth | null;
  /** The journal that put it in the books; null on a draft, and on an invoice saved before invoices were booked. */
  journalId: string | null;
  /** What was saved, number included. */
  draft: InvoiceDraft;
  /** When it first left: emailed, sent on WhatsApp or downloaded. Null: never sent. */
  sentAt: number | null;
  /** Why it was voided or discarded, and when. */
  voidReason: string;
  voidedAt: number | null;
  createdAt: number | null;
  createdBy: string;
}

const text = (v: unknown, max: number): string => (typeof v === "string" ? v.slice(0, max) : "");
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

const toCarePlanMonth = (v: unknown): CarePlanMonth | null => {
  const c = obj(v);
  const month = c.month;
  if (typeof month !== "number" || !Number.isInteger(month) || month < 1 || !isIsoDate(c.dueOn)) return null;
  const holds = Array.isArray(c.holds) ? PLAN_HOLD_REASONS.filter((r) => (c.holds as unknown[]).includes(r)) : [];
  return { month, dueOn: c.dueOn, holds };
};

const millis = (v: unknown): number | null => {
  if (typeof v === "number") return v;
  const m = (v as { toMillis?: () => number } | null)?.toMillis;
  return typeof m === "function" ? m.call(v) : null;
};

/** Reads a stored invoice defensively; null when it has no lines, or no number and is not a draft. */
export const toSavedInvoice = (id: string, data: unknown): SavedInvoice | null => {
  const d = obj(data);
  const inv = obj(d.draft);
  const number = text(d.number, 20);
  const stored = SAVED_INVOICE_STATUSES.find((s) => s === d.status);
  // Invoices saved before drafts existed have a number and no status: they were issued.
  const status: SavedInvoiceStatus | null = stored ?? (number ? "issued" : null);
  if (!status || !Array.isArray(inv.lines) || (!number && status !== "draft" && status !== "discarded")) return null;
  const client = obj(inv.client);
  const bank = obj(inv.bank);
  const draft: InvoiceDraft = {
    number,
    issuedOn: isIsoDate(inv.issuedOn) ? inv.issuedOn : "",
    dueOn: isIsoDate(inv.dueOn) ? inv.dueOn : "",
    client: {
      name: text(client.name, 200),
      address: text(client.address, 500),
      email: text(client.email, 300),
      registrationNo: text(client.registrationNo, 60),
    },
    contractReference: text(inv.contractReference, 60),
    paymentReference: text(inv.paymentReference, 80),
    lines: inv.lines.slice(0, 30).map((l) => {
      const line = obj(l);
      return { description: text(line.description, 300), cents: Number.isInteger(line.cents) ? (line.cents as number) : 0 };
    }),
    notes: text(inv.notes, 1000),
    bank: {
      bankName: text(bank.bankName, 120),
      accountHolder: text(bank.accountHolder, 120),
      accountNumber: text(bank.accountNumber, 40),
      branchCode: text(bank.branchCode, 20),
      accountType: text(bank.accountType, 60),
    },
  };
  const maybe = (v: unknown) => (typeof v === "string" && v !== "" ? v : null);
  return {
    id,
    number,
    status,
    contractId: maybe(d.contractId),
    clientId: maybe(d.clientId),
    retainerId: maybe(d.retainerId),
    carePlan: toCarePlanMonth(d.carePlan),
    journalId: maybe(d.journalId),
    draft,
    // Saved before invoices kept a send log (no sentAt field at all): it was made to be sent, so it counts as sent when it was saved.
    sentAt: "sentAt" in d ? millis(d.sentAt) : (millis(d.createdAt) ?? 0),
    voidReason: text(d.voidReason, 300),
    voidedAt: millis(d.voidedAt),
    createdAt: millis(d.createdAt),
    createdBy: text(d.createdBy, 128),
  };
};
